/**
 * Login Page — Unified progressive auth
 *
 * Step 1: single "Email or Phone" input
 *   type=email + exists  → password field
 *   type=email + !exists → prompt to sign up
 *   type=phone + exists  → WhatsApp OTP flow
 *   type=phone + !exists → route to /register?identifier=<phone>
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, User, Lock, KeyRound, Phone } from 'lucide-react';
import { useLogoUrl } from '../components/SAMALogo';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── helpers ────────────────────────────────────────────────────────
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function detectKind(value) {
  const v = (value || '').trim();
  if (!v) return { kind: 'unknown', normalized: '' };
  if (EMAIL_RE.test(v)) return { kind: 'email', normalized: v.toLowerCase() };
  const digits = v.replace(/\D+/g, '');
  const phone = digits.length > 10 && digits.startsWith('91') ? digits.slice(2) : digits;
  if (phone.length === 10) return { kind: 'phone', normalized: phone };
  return { kind: 'unknown', normalized: v };
}

const SAMALogo = () => {
  const logoUrl = useLogoUrl();
  return (
    <div className="flex items-center justify-center">
      <img
        src={logoUrl}
        alt="SAMA - The Calm Mind Studio"
        className="h-32 md:h-36 w-auto object-contain"
        style={{ imageRendering: '-webkit-optimize-contrast' }}
      />
    </div>
  );
};

// ─── page ───────────────────────────────────────────────────────────
export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  // step: 'identifier' → 'password' | 'otp'
  const [step, setStep] = useState('identifier');
  const [identifier, setIdentifier] = useState(searchParams.get('identifier') || '');
  const [kind, setKind] = useState('unknown');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // ─── Step 1: lookup identifier ───────────────────────────────────
  async function handleIdentifierSubmit(e) {
    e.preventDefault();
    const { kind: k, normalized } = detectKind(identifier);
    if (k === 'unknown') {
      toast.error('Enter a valid email or 10-digit phone number');
      return;
    }
    setIsLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/identifier-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ identifier: normalized }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Lookup failed');

      setKind(data.type);
      setIdentifier(data.identifier);

      if (!data.exists) {
        // Redirect to signup with the identifier prefilled
        toast.error(
          data.type === 'email'
            ? 'This email is not registered. Please sign up.'
            : 'This phone number is not registered. Please sign up.'
        );
        navigate(`/register?identifier=${encodeURIComponent(data.identifier)}`);
        return;
      }

      if (data.type === 'email') {
        if (!data.has_password) {
          toast.error('This account has no password set. Try Google Sign-In or reset your password.');
          return;
        }
        setStep('password');
      } else {
        // Phone flow → send OTP
        await requestOtp(data.identifier);
        setStep('otp');
      }
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  // ─── OTP helpers ─────────────────────────────────────────────────
  async function requestOtp(phone) {
    const r = await fetch(`${API}/api/auth/otp/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ identifier: phone }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.detail || 'Could not send OTP');
    toast.success('OTP sent via WhatsApp (also emailed as backup if you have an account)');
  }

  async function handleResendOtp() {
    setIsLoading(true);
    try {
      await requestOtp(identifier);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Step 2a: password submit ────────────────────────────────────
  async function handlePasswordSubmit(e) {
    e.preventDefault();
    if (!password) return toast.error('Enter your password');
    setIsLoading(true);
    try {
      const result = await login(identifier, password);
      toast.success('Welcome back!');
      routeAfterLogin(result);
    } catch (err) {
      toast.error(err.message || 'Wrong password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  // ─── Step 2b: OTP submit ─────────────────────────────────────────
  async function handleOtpSubmit(e) {
    e.preventDefault();
    if (!/^\d{4}$/.test(otp)) return toast.error('Enter the 4-digit OTP');
    setIsLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ identifier, otp }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Invalid OTP. Please try again.');
      // Hand off to AuthContext so the app is aware immediately.
      await login(data);
      toast.success('Welcome back!');
      routeAfterLogin(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  function routeAfterLogin(user) {
    const hasRole = user?.is_admin
      || (user?.permissions && Object.keys(user.permissions).length > 0);
    if (hasRole) {
      navigate('/admin', { replace: true });
      return;
    }
    // Regular members must complete onboarding before hitting /schedule.
    if (user?.profile_complete === false) {
      navigate('/account-preferences', { replace: true });
      return;
    }
    navigate('/schedule', { replace: true });
  }

  // ─── Google OAuth (kept as-is) ───────────────────────────────────
  async function handleGoogleLogin() {
    setIsGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;
    } catch (err) {
      toast.error(err.message || 'Google sign-in failed');
      setIsGoogleLoading(false);
    }
  }

  const goBackToIdentifier = () => {
    setStep('identifier');
    setPassword('');
    setOtp('');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="relative z-10 p-6">
        <button
          onClick={() => (step === 'identifier' ? navigate('/') : goBackToIdentifier())}
          className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10"><SAMALogo /></div>
          <div className="text-center mb-8">
            <h1 className="text-2xl font-heading text-foreground">Welcome Back</h1>
            <p className="text-muted-foreground mt-1">Sign in to your account</p>
          </div>

          {step === 'identifier' && (
            <>
              <button
                className="w-full flex items-center justify-center gap-3 h-14 bg-white rounded-2xl border border-border shadow-soft hover:shadow-card transition-all mb-6"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                data-testid="google-login-btn"
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span className="font-medium text-foreground">Continue with Google</span>
                  </>
                )}
              </button>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-4 text-muted-foreground">or</span>
                </div>
              </div>

              <form onSubmit={handleIdentifierSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="identifier" className="text-sm font-medium text-foreground">
                    Phone or Email
                  </Label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                    <Input
                      id="identifier"
                      autoFocus
                      inputMode="text"
                      placeholder="9876543210 or you@example.com"
                      className="sama-input h-14"
                      style={{ paddingLeft: '3rem' }}
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      required
                      data-testid="identifier-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full sama-btn-primary h-14 text-base"
                  disabled={isLoading}
                  data-testid="identifier-submit-btn"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                </Button>
              </form>
            </>
          )}

          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <div className="bg-secondary/40 rounded-xl px-4 py-3 text-sm text-muted-foreground flex items-center justify-between">
                <span className="truncate" data-testid="identifier-echo">{identifier}</span>
                <button type="button" onClick={goBackToIdentifier} className="text-primary text-xs font-medium">
                  Change
                </button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                  <Input
                    id="password"
                    type="password"
                    autoFocus
                    placeholder="••••••••"
                    className="sama-input h-14"
                    style={{ paddingLeft: '3rem' }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full sama-btn-primary h-14 text-base"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                <Link to="/forgot-password" className="text-primary font-medium hover:underline">
                  Forgot your password?
                </Link>
              </p>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleOtpSubmit} className="space-y-5">
              <div className="bg-secondary/40 rounded-xl px-4 py-3 text-sm text-muted-foreground flex items-center justify-between">
                <span className="flex items-center gap-2 truncate">
                  <Phone className="w-4 h-4 text-primary" />
                  <span data-testid="identifier-echo">+91 {identifier}</span>
                </span>
                <button type="button" onClick={goBackToIdentifier} className="text-primary text-xs font-medium">
                  Change
                </button>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-sm font-medium text-foreground">
                  Enter 4-digit OTP sent on WhatsApp
                </Label>
                <p className="text-xs text-muted-foreground -mt-1">
                  Also emailed to you as a backup — check your inbox if WhatsApp is delayed.
                </p>
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                  <Input
                    id="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={4}
                    autoFocus
                    placeholder="0000"
                    className="sama-input h-14 tracking-[0.4em] text-center text-lg"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    required
                    data-testid="otp-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full sama-btn-primary h-14 text-base"
                disabled={isLoading || otp.length !== 4}
                data-testid="otp-submit-btn"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Submit'}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Didn't get it?{' '}
                <button type="button" onClick={handleResendOtp} className="text-primary font-medium hover:underline" data-testid="otp-resend-btn">
                  Resend OTP
                </button>
              </p>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-primary font-medium hover:underline">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
